import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { useState } from "react";

import { PageContainer } from "@/components/ui/page-container";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";
import { isoDate } from "@/lib/iso-date";
import { fallback, optional, pageParam } from "@/lib/search-params";

import { FinanceAreaAction } from "./-components/financeiro/financeiro-v2/actions";
import { BankArea } from "./-components/financeiro/financeiro-v2/bank-area";
import { BankResolutionModal } from "./-components/financeiro/financeiro-v2/bank-resolution-modal";
import { BillModal } from "./-components/financeiro/financeiro-v2/bill-modal";
import { BillsArea } from "./-components/financeiro/financeiro-v2/bills-area";
import { CashArea } from "./-components/financeiro/financeiro-v2/cash-area";
import { CashBillPaymentModal } from "./-components/financeiro/financeiro-v2/cash-bill-payment-modal";
import { CashCountModal } from "./-components/financeiro/financeiro-v2/cash-count-modal";
import { CashDepositEvidenceModal } from "./-components/financeiro/financeiro-v2/cash-deposit-evidence-modal";
import { CashMovementModal } from "./-components/financeiro/financeiro-v2/cash-movement-modal";
import { EvidenceModal } from "./-components/financeiro/financeiro-v2/evidence-modal";
import { OverviewArea } from "./-components/financeiro/financeiro-v2/overview-area";
import { ReconciliationArea } from "./-components/financeiro/financeiro-v2/reconciliation-area";
import { ReceiptModal } from "./-components/financeiro/financeiro-v2/receipt-modal";
import { ReceiptsArea } from "./-components/financeiro/financeiro-v2/receipts-area";
import { RedeSalesImportModal } from "./-components/financeiro/financeiro-v2/rede-sales-import-modal";
import { SettlementModal } from "./-components/financeiro/financeiro-v2/settlement-modal";
import { FinanceShell } from "./-components/financeiro/financeiro-v2/shell";
import type { FinanceArea, FinanceModal } from "./-components/financeiro/financeiro-v2/types";

const area = type.enumerated("cash", "overview", "receipts", "bills", "bank", "reconciliation");
const searchSchema = type({
	month: fallback(type(/^\d{4}-\d{2}$/u), format.today().slice(0, 7)),
	day: fallback(isoDate, format.today()),
	area: fallback(area, "cash"),
	page: pageParam,
	"account?": optional(type("number.integer > 0")),
});

export const Route = createFileRoute("/_app/financeiro")({
	validateSearch: searchSchema,
	component: FinanceiroPage,
});

function FinanceiroPage() {
	const navigate = Route.useNavigate();
	const { month, day, area: activeArea, page, account } = Route.useSearch();
	const [modal, setModal] = useState<FinanceModal>(null);
	const selectedMonth = activeArea === "cash" ? day.slice(0, 7) : month;
	const onArea = (next: FinanceArea) => {
		navigate({
			search: (previous) => ({
				...previous,
				area: next,
				month:
					previous.area === "cash" || next === "cash" ? previous.day.slice(0, 7) : previous.month,
				page: 1,
			}),
		});
	};
	const onAccount = (next?: number) => {
		navigate({ search: (previous) => ({ ...previous, account: next, page: 1 }) });
	};
	const onMonth = (next: string) => {
		navigate({
			search: (previous) => {
				const nextMonth = djs(`${next}-01`, "YYYY-MM-DD");
				const nextDay = nextMonth
					.date(Math.min(djs(previous.day, "YYYY-MM-DD").date(), nextMonth.daysInMonth()))
					.format("YYYY-MM-DD");

				return { ...previous, month: next, day: nextDay, page: 1 };
			},
		});
	};
	const onDay = (next: string) => {
		navigate({
			search: (previous) => ({
				...previous,
				day: next,
				month: next.slice(0, 7),
				page: 1,
			}),
		});
	};
	const onPage = (next: number) => {
		navigate({ search: (previous) => ({ ...previous, page: next }) });
	};

	return (
		<PageContainer>
			<FinanceShell
				area={activeArea}
				month={selectedMonth}
				day={day}
				onArea={onArea}
				onMonth={onMonth}
				onDay={onDay}
				action={<FinanceAreaAction area={activeArea} onModal={setModal} />}
			>
				{activeArea === "cash" && <CashArea date={day} onModal={setModal} />}
				{activeArea === "overview" && (
					<OverviewArea month={selectedMonth} onArea={onArea} onModal={setModal} />
				)}
				{activeArea === "receipts" && (
					<ReceiptsArea month={selectedMonth} page={page} onPage={onPage} onModal={setModal} />
				)}
				{activeArea === "bills" && (
					<BillsArea month={selectedMonth} page={page} onPage={onPage} onModal={setModal} />
				)}
				{activeArea === "bank" && (
					<BankArea
						month={selectedMonth}
						page={page}
						account={account}
						onPage={onPage}
						onAccount={onAccount}
						onModal={setModal}
					/>
				)}
				{activeArea === "reconciliation" && (
					<ReconciliationArea
						month={selectedMonth}
						page={page}
						onPage={onPage}
						onModal={setModal}
					/>
				)}
			</FinanceShell>

			{modal?.kind === "receipt" && (
				<ReceiptModal initial={modal.initial} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "bill" && (
				<BillModal initial={modal.initial} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "evidence" && (
				<EvidenceModal target={modal.target} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "bank-resolution" && (
				<BankResolutionModal id={modal.id} onModal={setModal} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "settlement" && (
				<SettlementModal id={modal.id} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "cash-movement" && (
				<CashMovementModal
					kind={modal.movementKind}
					date={modal.date}
					onClose={() => setModal(null)}
				/>
			)}
			{modal?.kind === "cash-bill-payment" && (
				<CashBillPaymentModal date={modal.date} onClose={() => setModal(null)} />
			)}
			{modal?.kind === "cash-count" && (
				<CashCountModal
					date={modal.date}
					balanceCents={modal.balanceCents}
					onClose={() => setModal(null)}
				/>
			)}
			{modal?.kind === "cash-deposit-evidence" && (
				<CashDepositEvidenceModal
					id={modal.id}
					linked={modal.linked}
					onClose={() => setModal(null)}
				/>
			)}
			{modal?.kind === "rede-sales-import" && (
				<RedeSalesImportModal onClose={() => setModal(null)} />
			)}
		</PageContainer>
	);
}
